import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Message,
  Space,
  Alert,
  Upload,
  Card,
  Tag,
  Progress
} from '@arco-design/web-react';
import {
  IconUser,
  IconPhone,
  IconHome,
  IconFile,
  IconUpload,
  IconCheck,
  IconClose
} from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '@/config/api';
import {
  useGeographyStates,
  useSaveIdentity,
  useUploadDocument,
  useDeleteDocument
} from '../hooks/useOnboardingData';
import {
  PRIMARY_COUNTRIES,
  EXTENDED_COUNTRIES,
  COUNTRY_OPTIONS,
  COUNTRY_STATE_OPTIONS,
  buildStateOptions,
  getCityOptions,
  getCountryCode
} from '../data/geography';
import './IdentityStep.css';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';
import ReportProblemButton from '../components/ReportProblemButton';

const FormItem = Form.Item;
const { Option } = Select;
const CUSTOM_OPTION_VALUE = '__CUSTOM__';
const PRIMARY_COUNTRY_CODES = [
  { value: '+52', label: 'México' },
  { value: '+1', label: 'USA' },
  { value: '+57', label: 'Colombia' },
  { value: '+56', label: 'Chile' },
  { value: '+34', label: 'España' }
];
const LATAM_COUNTRY_CODES = [
  { value: '+51', label: 'Perú' },
  { value: '+52', label: 'México' },
  { value: '+53', label: 'Cuba' },
  { value: '+54', label: 'Argentina' },
  { value: '+55', label: 'Brasil' },
  { value: '+56', label: 'Chile' },
  { value: '+57', label: 'Colombia' },
  { value: '+58', label: 'Venezuela' },
  { value: '+501', label: 'Belice' },
  { value: '+502', label: 'Guatemala' },
  { value: '+503', label: 'El Salvador' },
  { value: '+504', label: 'Honduras' },
  { value: '+505', label: 'Nicaragua' },
  { value: '+506', label: 'Costa Rica' },
  { value: '+507', label: 'Panamá' },
  { value: '+509', label: 'Haití' },
  { value: '+591', label: 'Bolivia' },
  { value: '+592', label: 'Guyana' },
  { value: '+593', label: 'Ecuador' },
  { value: '+594', label: 'Guayana Francesa' },
  { value: '+595', label: 'Paraguay' },
  { value: '+597', label: 'Surinam' },
  { value: '+598', label: 'Uruguay' }
];
const PRIMARY_COUNTRY_CODE_VALUES = new Set(
  PRIMARY_COUNTRY_CODES.map((item) => item.value)
);
const parseCountryCodeValue = (value) => {
  const numeric = parseInt(String(value || '').replace('+', ''), 10);
  return Number.isNaN(numeric) ? 0 : numeric;
};
const EXTRA_LATAM_COUNTRY_CODES = [...LATAM_COUNTRY_CODES]
  .sort((a, b) => parseCountryCodeValue(a.value) - parseCountryCodeValue(b.value))
  .filter((item) => !PRIMARY_COUNTRY_CODE_VALUES.has(item.value));

function IdentityStep({ onNext, initialData, documents: initialDocuments, requestedChanges, previewMode = false, freeMode = false }) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();

  const saveMutation = useSaveIdentity();
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();

  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [countryCode, setCountryCode] = useState('MX');
  const [stateValue, setStateValue] = useState('');
  const [useCustomState, setUseCustomState] = useState(false);
  const [useCustomCity, setUseCustomCity] = useState(false);
  const [governmentIdType, setGovernmentIdType] = useState('CURP');
  const [showAllCountryCodes, setShowAllCountryCodes] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [documents, setDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const isHydrating = useRef(false);

  const statesQuery = useGeographyStates(countryCode);
  const stateOptions = useMemo(() => {
    if (countryCode === 'MX') return statesQuery.data || [];
    if (COUNTRY_STATE_OPTIONS[countryCode]) return buildStateOptions(COUNTRY_STATE_OPTIONS[countryCode]);
    return [];
  }, [countryCode, statesQuery.data]);

  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'identity' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);
  const documentChangeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'documents' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);
  const countryCodeOptions = useMemo(() => {
    return showAllCountryCodes
      ? [...PRIMARY_COUNTRY_CODES, ...EXTRA_LATAM_COUNTRY_CODES]
      : PRIMARY_COUNTRY_CODES;
  }, [showAllCountryCodes]);
  const renderCountryCodeDropdown = (menu) => (
    <div>
      {menu}
      <div className="phone-prefix-footer" onMouseDown={(event) => event.preventDefault()}>
        <Button
          type="text"
          size="mini"
          onClick={() => setShowAllCountryCodes((prev) => !prev)}
        >
          {showAllCountryCodes ? t('steps.identity.options.showLessCountries') : t('steps.identity.options.showMoreCountries')}
        </Button>
      </div>
    </div>
  );

  const countryOptions = useMemo(() => {
    return showAllCountries
      ? [...PRIMARY_COUNTRIES, ...EXTENDED_COUNTRIES]
      : PRIMARY_COUNTRIES;
  }, [showAllCountries]);
  const renderCountryDropdown = (menu) => (
    <div>
      {menu}
      <div className="phone-prefix-footer" onMouseDown={(event) => event.preventDefault()}>
        <Button
          type="text"
          size="mini"
          onClick={() => setShowAllCountries((prev) => !prev)}
        >
          {showAllCountries ? t('steps.identity.options.showLessCountries') : t('steps.identity.options.showMoreCountries')}
        </Button>
      </div>
    </div>
  );

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  // Auto-save cada 30 segundos
  useEffect(() => {
    autoSaveTimerRef.current = setInterval(() => {
      const values = form.getFieldsValue();
      // Solo auto-guardar si hay al menos algunos campos completos
      const hasData = Object.values(values).some(v => v !== undefined && v !== null && v !== '');
      if (hasData && !saveMutation.isPending) {
        handleAutoSave(values);
      }
    }, 30000); // 30 segundos

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!stateValue) {
      setCityOptions([]);
      setUseCustomCity(false);
      return;
    }
    setCityOptions(getCityOptions(countryCode, stateValue));
  }, [countryCode, stateValue]);

  useEffect(() => {
    if (!stateValue) return;
    const isKnown = stateOptions.some((option) => option.value === stateValue);
    setUseCustomState(!isKnown);
  }, [stateOptions, stateValue]);

  useEffect(() => {
    const cityValue = form.getFieldValue('city');
    if (!cityValue || cityValue === CUSTOM_OPTION_VALUE) {
      return;
    }
    const isKnownCity = cityOptions.some((option) => option.value === cityValue);
    setUseCustomCity(!isKnownCity);
    if (!isKnownCity) {
      form.setFieldsValue({
        city: CUSTOM_OPTION_VALUE,
        city_other: cityValue
      });
    }
  }, [cityOptions, form]);

  // Cargar datos iniciales si existen
  useEffect(() => {
    if (initialData) {
      isHydrating.current = true;
      const resolvedCountry = initialData.country || 'México';
      const resolvedCountryCode = getCountryCode(resolvedCountry) || null;
      // If the saved country is not in the lists, expand to show all countries
      const isInPrimary = PRIMARY_COUNTRIES.some((c) => c.value === resolvedCountry);
      const isInExtended = EXTENDED_COUNTRIES.some((c) => c.value === resolvedCountry);
      if (!isInPrimary && isInExtended) {
        setShowAllCountries(true);
      }
      form.setFieldsValue({
        first_name: initialData.first_name || '',
        middle_name: initialData.middle_name || '',
        last_name: initialData.last_name || '',
        second_last_name: initialData.second_last_name || '',
        date_of_birth: initialData.date_of_birth || null,
        gender: initialData.gender || undefined,
        phone_country_code: initialData.phone_country_code || '+52',
        phone: initialData.phone || '',
        emergency_contact_name: initialData.emergency_contact_name || '',
        emergency_contact_relationship: initialData.emergency_contact_relationship || undefined,
        emergency_phone_country_code: initialData.emergency_phone_country_code || '+52',
        emergency_phone: initialData.emergency_phone || '',
        government_id: initialData.government_id || '',
        government_id_type: initialData.government_id_type || 'CURP',
        address: initialData.address || '',
        city: initialData.city || '',
        state: initialData.state || '',
        postal_code: initialData.postal_code || '',
        country: resolvedCountry
      });

      setCountryCode(resolvedCountryCode);
      setStateValue(initialData.state || '');
      setGovernmentIdType(initialData.government_id_type || 'CURP');
      setTimeout(() => {
        isHydrating.current = false;
      }, 0);
    }
  }, [initialData, form]);

  useEffect(() => {
    setDocuments(initialDocuments || {});
  }, [initialDocuments]);

  const handleValuesChange = (changedValues) => {
    if (isHydrating.current) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(changedValues, 'country')) {
      const nextCountry = changedValues.country || 'México';
      setCountryCode(getCountryCode(nextCountry) || null);
      setStateValue('');
      setUseCustomState(false);
      setUseCustomCity(false);
      form.setFieldsValue({ state: '', city: '' });
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'state')) {
      const nextState = changedValues.state || '';
      const selectedCountry = form.getFieldValue('country');
      const effectiveCountryCode = getCountryCode(selectedCountry) || countryCode;
      setStateValue(nextState);
      setCityOptions(getCityOptions(effectiveCountryCode, nextState));
      form.setFieldsValue({ city: '' });
      setUseCustomCity(false);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'city')) {
      const nextCity = changedValues.city || '';
      const isCustomCity = nextCity === CUSTOM_OPTION_VALUE;
      setUseCustomCity(isCustomCity);
      if (!isCustomCity) {
        form.setFieldsValue({ city_other: '' });
      }
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'government_id_type')) {
      const nextType = changedValues.government_id_type || 'CURP';
      setGovernmentIdType(nextType);
    }
  };

  const handleUpload = async (file, documentKey) => {
    if (previewMode) {
      setDocuments(prev => ({
        ...prev,
        [documentKey]: {
          file_name: file?.name || t('shared.labels.document'),
          file_url: '',
          file_path: '',
          file_type: file?.type || ''
        }
      }));
      Message.success(t('shared.messages.simulationDocUploaded'));
      return false;
    }

    setUploadProgress(prev => ({ ...prev, [documentKey]: 0 }));

    uploadMutation.mutate(
      { file, documentType: documentKey },
      {
        onSuccess: (response) => {
          const uploadedDocument = response?.data?.document;
          if (!uploadedDocument) {
            Message.error(t('shared.messages.documentUploadError'));
            setUploadProgress(prev => {
              const next = { ...prev };
              delete next[documentKey];
              return next;
            });
            return;
          }

          setDocuments(prev => ({
            ...prev,
            [documentKey]: uploadedDocument
          }));

          Message.success(t('shared.messages.documentUploaded'));

          setTimeout(() => {
            setUploadProgress(prev => {
              const next = { ...prev };
              delete next[documentKey];
              return next;
            });
          }, 800);
        },
        onError: (error) => {
          console.error('Error al subir documento:', error);
          Message.error(t('shared.messages.documentUploadError'));
          setUploadProgress(prev => {
            const next = { ...prev };
            delete next[documentKey];
            return next;
          });
        }
      }
    );

    return false;
  };

  const handleDelete = async (documentKey) => {
    if (previewMode) {
      setDocuments(prev => {
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      Message.success(t('shared.messages.simulationDocDeleted'));
      return;
    }
    deleteMutation.mutate(documentKey, {
      onSuccess: () => {
        setDocuments(prev => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      },
      onError: (error) => {
        console.error('Error al eliminar documento:', error);
        Message.error(t('shared.messages.documentDeleteError'));
      }
    });
  };

  const buildFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('http')) return fileUrl;
    return `${getApiUrl()}${fileUrl}`;
  };

  const getFileExtension = (fileName = '') => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const renderDocumentThumbnail = (doc) => {
    const fileUrl = buildFileUrl(doc?.file_url || doc?.file_path || '');
    const fileName = doc?.file_name || t('shared.labels.document');
    const extension = getFileExtension(fileName);
    const isPdf = extension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
    const extensionLabel = extension ? extension.toUpperCase() : t('shared.labels.file');

    if (!fileUrl) {
      return (
        <div className="document-thumbnail-fallback">
          <IconFile />
          <span>{t('shared.labels.noPreview')}</span>
        </div>
      );
    }

    if (isImage) {
      return (
        <img
          src={fileUrl}
          alt={fileName}
          className="document-thumbnail-image"
          loading="lazy"
        />
      );
    }

    if (isPdf) {
      return (
        <object data={fileUrl} type="application/pdf" className="document-thumbnail-frame">
          <div className="document-thumbnail-fallback">
            <IconFile />
            <span>PDF</span>
          </div>
        </object>
      );
    }

    return (
      <div className="document-thumbnail-fallback">
        <IconFile />
        <span>{extensionLabel}</span>
      </div>
    );
  };

  const openPreview = (doc, title) => {
    if (!doc) return;
    setPreviewDoc(doc);
    setPreviewTitle(title || doc.file_name || t('shared.labels.document'));
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewDoc(null);
    setPreviewTitle('');
  };

  const renderIdDocumentCard = () => {
    const documentKey = 'curp';
    const isUploaded = !!documents[documentKey];
    const isUploading = uploadMutation.isPending && uploadMutation.variables?.documentType === documentKey;
    const progress = uploadProgress[documentKey];
    const changeMessage = documentChangeMap[documentKey];
    const previewLabel = t('steps.identity.labels.officialId');
    const description = governmentIdType === 'CURP'
      ? t('steps.identity.documents.idDescription')
      : t('steps.identity.documents.idDescriptionOther');

    return (
      <Card className={`document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{t('steps.identity.labels.officialId')}</span>
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>
          </div>
          {isUploaded && (
            <Tag color="green" icon={<IconCheck />}>
              {t('shared.labels.uploaded')}
            </Tag>
          )}
        </div>

        <div className="document-description">
          <small>{description}</small>
        </div>
        {changeMessage && (
          <div className="document-change-note">{changeMessage}</div>
        )}

        {(progress !== undefined || isUploading) && (
          <Progress
            percent={progress !== undefined ? progress : 0}
            status={progress === 100 ? 'success' : 'normal'}
            style={{ marginTop: 12 }}
          />
        )}

        {!isUploaded && progress === undefined && !isUploading && (
          <Upload
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => handleUpload(file, documentKey)}
            showUploadList={false}
          >
            <Button
              type="outline"
              icon={<IconUpload />}
              style={{ marginTop: 12, width: '100%' }}
            >
              {t('shared.buttons.selectFile')}
            </Button>
          </Upload>
        )}

        {isUploaded && (
          <div className="document-actions">
            <div className="document-info">
              <button
                type="button"
                className="document-thumbnail"
                onClick={() => openPreview(documents[documentKey], previewLabel)}
                aria-label={t('shared.labels.viewDocument')}
              >
                {renderDocumentThumbnail(documents[documentKey])}
              </button>
              <div className="document-meta">
                <small>{documents[documentKey].file_name}</small>
                <small>{(documents[documentKey].file_size / 1024).toFixed(1)} KB</small>
              </div>
            </div>
            <Button
              type="text"
              status="danger"
              icon={<IconClose />}
              onClick={() => handleDelete(documentKey)}
            >
              {t('shared.labels.delete')}
            </Button>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.fileFormats')}</small>
        </div>
      </Card>
    );
  };

  const renderPhotoDocumentCard = () => {
    const documentKey = 'photo';
    const isUploaded = !!documents[documentKey];
    const isUploading = uploadMutation.isPending && uploadMutation.variables?.documentType === documentKey;
    const progress = uploadProgress[documentKey];
    const changeMessage = documentChangeMap[documentKey];
    const previewLabel = t('steps.identity.labels.profilePhoto');

    return (
      <Card className={`document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{t('steps.identity.labels.profilePhoto')}</span>
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>
          </div>
          {isUploaded && (
            <Tag color="green" icon={<IconCheck />}>
              {t('shared.labels.uploaded')}
            </Tag>
          )}
        </div>

        <div className="document-description">
          <small>{t('steps.identity.documents.photoDescription')}</small>
        </div>
        {changeMessage && (
          <div className="document-change-note">{changeMessage}</div>
        )}

        {(progress !== undefined || isUploading) && (
          <Progress
            percent={progress !== undefined ? progress : 0}
            status={progress === 100 ? 'success' : 'normal'}
            style={{ marginTop: 12 }}
          />
        )}

        {!isUploaded && progress === undefined && !isUploading && (
          <Upload
            accept=".jpg,.jpeg,.png"
            beforeUpload={(file) => handleUpload(file, documentKey)}
            showUploadList={false}
          >
            <Button
              type="outline"
              icon={<IconUpload />}
              style={{ marginTop: 12, width: '100%' }}
            >
              {t('shared.buttons.selectFile')}
            </Button>
          </Upload>
        )}

        {isUploaded && (
          <div className="document-actions">
            <div className="document-info">
              <button
                type="button"
                className="document-thumbnail"
                onClick={() => openPreview(documents[documentKey], previewLabel)}
                aria-label={t('shared.labels.viewDocument')}
              >
                {renderDocumentThumbnail(documents[documentKey])}
              </button>
              <div className="document-meta">
                <small>{documents[documentKey].file_name}</small>
                <small>{(documents[documentKey].file_size / 1024).toFixed(1)} KB</small>
              </div>
            </div>
            <Button
              type="text"
              status="danger"
              icon={<IconClose />}
              onClick={() => handleDelete(documentKey)}
            >
              {t('shared.labels.delete')}
            </Button>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.imageFormats')}</small>
        </div>
      </Card>
    );
  };

  const normalizeIdentityPayload = (values) => {
    const payload = { ...values };

    if (payload.date_of_birth && typeof payload.date_of_birth === 'string') {
      const parts = payload.date_of_birth.split('/');
      if (parts.length === 3) {
        payload.date_of_birth = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    return payload;
  };

  const persistIdentity = (values, { advance }) => {
    if (previewMode) {
      Message.success(advance ? t('shared.messages.simulationIdentityValidated') : t('shared.messages.simulationIdentitySaved'));
      if (advance) {
        onNext(null);
      }
      return;
    }
    const payload = normalizeIdentityPayload(values);
    saveMutation.mutate(payload, {
      onSuccess: (response) => {
        const data = response?.data || {};

        if (data.duplicate_identity_found) {
          setDuplicateWarning(data.duplicate_identity_found?.message || null);
        }

        Message.success(advance ? t('shared.messages.identitySaved') : t('shared.messages.identitySavedDraft'));

        if (advance) {
          onNext(null);
        }
      },
      onError: (error) => {
        console.error('Error al guardar identidad:', error);
        Message.error(t('shared.messages.identityError'));
      }
    });
  };

  const handleAutoSave = (values) => {
    if (previewMode) {
      return;
    }
    const payload = normalizeIdentityPayload(values);
    saveMutation.mutate(payload, {
      onSuccess: () => {
      },
      onError: (error) => {
        console.error('Error en auto-guardado:', error);
      }
    });
  };

  const validateCURP = (value) => {
    if (!value) return true; // Permitir vacío (required lo maneja)

    // Formato CURP: 18 caracteres alfanuméricos
    const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z][0-9]$/;

    if (!curpRegex.test(value)) {
      return false;
    }

    return true;
  };

  const validateAge = (date) => {
    if (!date) return true;

    const today = new Date();
    const birthDate = new Date(date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // Mínimo 18 años
    return age >= 18;
  };

  const handleSubmit = (values) => {
    setDuplicateWarning(null);
    persistIdentity(values, { advance: true });
  };

  const handleSaveDraft = () => {
    setDuplicateWarning(null);

    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }
    const values = form.getFieldsValue();
    persistIdentity(values, { advance: false });
  };

  const handleResetForm = () => {
    form.resetFields();
    setUseCustomCity(false);
    setUseCustomState(false);
    setStateValue('');
    setCountryCode('MX');
    setCityOptions([]);
    setGovernmentIdType('CURP');
  };

  return (
    <div className="identity-step">
      <div className="step-header">
        <h2>{t('steps.identity.title')}</h2>
        <p>{t('steps.identity.description')}</p>
      </div>

      {duplicateWarning && (
        <Alert
          type="warning"
          content={duplicateWarning}
          closable
          onClose={() => setDuplicateWarning(null)}
          style={{ marginBottom: 20 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        {/* Datos Personales */}
        <div className="form-section">
          <h3>
            <IconUser style={{ marginRight: 8 }} />
            {t('steps.identity.labels.personalInfo')}
          </h3>

          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.firstName')}
              field="first_name"
              rules={[
                { required: true, message: t('steps.identity.validation.nameRequired') },
                { minLength: 2, message: t('steps.identity.validation.minTwoChars') }
              ]}
              {...getFieldProps('first_name')}
            >
              <Input placeholder={t('steps.identity.placeholders.firstName')} />
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.middleName')}
              field="middle_name"
              {...getFieldProps('middle_name')}
            >
              <Input placeholder={t('steps.identity.placeholders.middleName')} />
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.lastName')}
              field="last_name"
              rules={[
                { required: true, message: t('steps.identity.validation.lastNameRequired') }
              ]}
              {...getFieldProps('last_name')}
            >
              <Input placeholder={t('steps.identity.placeholders.lastName')} />
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.secondLastName')}
              field="second_last_name"
              {...getFieldProps('second_last_name')}
            >
              <Input placeholder={t('steps.identity.placeholders.secondLastName')} />
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.dateOfBirth')}
              field="date_of_birth"
              rules={[
                { required: true, message: t('steps.identity.validation.dobRequired') },
                {
                  validator: (value, cb) => {
                    if (!validateAge(value)) {
                      cb(t('steps.identity.validation.ageMinimum'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('date_of_birth')}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('steps.identity.placeholders.dateOfBirth')}
                format="DD/MM/YYYY"
                defaultPickerValue="2000-01-01"
              />
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.gender')}
              field="gender"
              rules={[
                { required: true, message: t('steps.identity.validation.genderRequired') }
              ]}
              {...getFieldProps('gender')}
            >
              <Select placeholder={t('steps.identity.placeholders.selectGender')}>
                <Option value="M">{t('steps.identity.options.male')}</Option>
                <Option value="F">{t('steps.identity.options.female')}</Option>
                <Option value="O">{t('steps.identity.options.other')}</Option>
              </Select>
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.idType')}
              field="government_id_type"
              initialValue="CURP"
              rules={[
                {
                  validator: (value, cb) => {
                    if (value === 'OTHER') {
                      cb(t('steps.identity.validation.indicateIdType'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('government_id_type')}
            >
              <Select>
                <Option value="CURP">{t('steps.identity.options.curp')}</Option>
                <Option value="INE">{t('steps.identity.options.ine')}</Option>
                <Option value="PASSPORT">{t('steps.identity.options.passport')}</Option>
                <Option value="OTHER">{t('steps.identity.options.otherIdType')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.idNumber')}
              field="government_id"
              rules={[
                { required: true, message: t('steps.identity.validation.idRequired') },
                {
                  validator: (value, cb) => {
                    const idType = form.getFieldValue('government_id_type');
                    if (idType === 'CURP' && !validateCURP(value)) {
                      cb(t('steps.identity.validation.curpInvalid'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('government_id')}
            >
              <Input
                placeholder={
                  governmentIdType === 'CURP'
                    ? t('steps.identity.placeholders.curpExample')
                    : governmentIdType === 'INE'
                      ? t('steps.identity.placeholders.ineExample')
                      : governmentIdType === 'PASSPORT'
                        ? t('steps.identity.placeholders.passportExample')
                        : t('steps.identity.placeholders.otherIdNumber')
                }
                style={
                  governmentIdType === 'CURP'
                    ? { textTransform: 'uppercase' }
                    : undefined
                }
              />
            </FormItem>
          </div>

          <div className="form-row form-row--phone">
            <FormItem
              label={t('steps.identity.labels.phonePrefix')}
              field="phone_country_code"
              initialValue="+52"
              className="phone-prefix"
              {...getFieldProps('phone_country_code')}
            >
              <Select
                showSearch
                dropdownRender={renderCountryCodeDropdown}
                filterOption={(inputValue, option) => {
                  const match = countryCodeOptions.find(o => o.value === option.props.value);
                  if (!match) return false;
                  const search = inputValue.toLowerCase();
                  return match.label.toLowerCase().includes(search) || match.value.includes(search);
                }}
              >
                {countryCodeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.value} ({option.label})
                  </Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.phone')}
              field="phone"
              rules={[
                { required: true, message: t('steps.identity.validation.phoneRequired') },
                {
                  match: /^[0-9]{7,15}$/,
                  message: t('steps.identity.validation.phoneInvalid')
                }
              ]}
              className="phone-number"
              {...getFieldProps('phone')}
            >
              <Input placeholder={t('steps.identity.placeholders.phone')} maxLength={15} />
            </FormItem>
          </div>

          <div className="documents-section">
            <h4>{t('steps.identity.labels.idDocumentSection')}</h4>
            {renderIdDocumentCard()}
            {renderPhotoDocumentCard()}
          </div>
        </div>

        {/* Dirección */}
        <div className="form-section">
          <h3>
            <IconHome style={{ marginRight: 8 }} />
            {t('steps.identity.labels.addressSection')}
          </h3>

          <FormItem
            label={t('steps.identity.labels.address')}
            field="address"
            rules={[
              { required: true, message: t('steps.identity.validation.addressRequired') }
            ]}
            {...getFieldProps('address')}
          >
            <Input placeholder={t('steps.identity.placeholders.address')} />
          </FormItem>

          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.country')}
              field="country"
              initialValue="México"
              rules={[
                { required: true, message: t('steps.identity.validation.countryRequired') }
              ]}
              {...getFieldProps('country')}
            >
              <Select
                showSearch
                dropdownRender={renderCountryDropdown}
                filterOption={(inputValue, option) => {
                  const label = option.props.children;
                  return typeof label === 'string' && label.toLowerCase().includes(inputValue.toLowerCase());
                }}
              >
                {countryOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.stateRegion')}
              field="state"
              rules={[
                { required: true, message: t('steps.identity.validation.stateRequired') }
              ]}
              {...getFieldProps('state')}
              extra={
                useCustomState && stateOptions.length > 0 ? (
                  <Button
                    type="text"
                    size="mini"
                    className="custom-field-toggle"
                    onClick={() => {
                      setUseCustomState(false);
                      setStateValue('');
                      form.setFieldValue('state', '');
                    }}
                  >
                    {t('steps.corporate.labels.chooseFromList')}
                  </Button>
                ) : null
              }
            >
              {useCustomState || stateOptions.length === 0 ? (
                <Input placeholder={t('steps.identity.placeholders.writeStateRegion')} />
              ) : (
                <Select
                  placeholder={t('steps.identity.placeholders.selectState')}
                  showSearch
                  loading={countryCode === 'MX' && statesQuery.isLoading}
                  onChange={(value) => {
                    if (value === CUSTOM_OPTION_VALUE) {
                      setUseCustomState(true);
                      form.setFieldValue('state', '');
                      return;
                    }
                    setStateValue(value);
                  }}
                >
                  {stateOptions.map((state) => (
                    <Option key={state.value} value={state.value}>
                      {state.label}
                    </Option>
                  ))}
                  <Option key={CUSTOM_OPTION_VALUE} value={CUSTOM_OPTION_VALUE}>
                    {t('steps.corporate.labels.other')}
                  </Option>
                </Select>
              )}
            </FormItem>
          </div>


          <div className="form-row">
            <FormItem
              label={t('steps.identity.labels.city')}
              field="city"
              rules={[
                { required: true, message: t('steps.identity.validation.cityRequired') },
                {
                  validator: (value, cb) => {
                    if (value === CUSTOM_OPTION_VALUE) {
                      cb(t('steps.identity.validation.cityCustom'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('city')}
            >
              <Select placeholder={t('steps.identity.placeholders.selectCity')} showSearch allowCreate>
                {cityOptions.map((city) => (
                  <Option key={city.value} value={city.value}>
                    {city.label}
                  </Option>
                ))}
                <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.postalCode')}
              field="postal_code"
              rules={[
                { required: true, message: t('steps.identity.validation.postalCodeRequired') },
                { match: /^[0-9]{4,10}$/, message: t('steps.identity.validation.postalCodeInvalid') }
              ]}
              {...getFieldProps('postal_code')}
            >
              <Input placeholder={t('steps.identity.placeholders.postalCode')} maxLength={10} />
            </FormItem>
          </div>

        </div>

        {/* Contacto de Emergencia */}
        <div className="form-section">
          <h3>
            <IconPhone style={{ marginRight: 8 }} />
            {t('steps.identity.labels.emergencyContact')}
          </h3>

          <FormItem
            label={t('steps.identity.labels.emergencyName')}
            field="emergency_contact_name"
            {...getFieldProps('emergency_contact_name')}
          >
            <Input placeholder={t('steps.identity.placeholders.emergencyName')} />
          </FormItem>

          <FormItem
            label={t('steps.identity.labels.emergencyRelationship')}
            field="emergency_contact_relationship"
            {...getFieldProps('emergency_contact_relationship')}
          >
            <Select placeholder={t('steps.identity.placeholders.emergencyRelationship')}>
              <Option value="familiar">{t('steps.identity.options.familiar')}</Option>
              <Option value="amistad">{t('steps.identity.options.friendship')}</Option>
              <Option value="pareja">{t('steps.identity.options.partner')}</Option>
              <Option value="otro">{t('steps.identity.options.otherRelation')}</Option>
            </Select>
          </FormItem>

          <div className="form-row form-row--phone">
            <FormItem
              label="Prefijo de País"
              field="emergency_phone_country_code"
              initialValue="+52"
              className="phone-prefix"
              {...getFieldProps('emergency_phone_country_code')}
            >
              <Select
                showSearch
                dropdownRender={renderCountryCodeDropdown}
                filterOption={(inputValue, option) => {
                  const match = countryCodeOptions.find(o => o.value === option.props.value);
                  if (!match) return false;
                  const search = inputValue.toLowerCase();
                  return match.label.toLowerCase().includes(search) || match.value.includes(search);
                }}
              >
                {countryCodeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.value} ({option.label})
                  </Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.identity.labels.emergencyPhone')}
              field="emergency_phone"
              rules={[
                {
                  match: /^[0-9]{7,15}$/,
                  message: t('steps.identity.validation.phoneInvalid')
                }
              ]}
              className="phone-number"
              {...getFieldProps('emergency_phone')}
            >
              <Input placeholder={t('steps.identity.placeholders.emergencyPhone')} maxLength={15} />
            </FormItem>
          </div>
        </div>

        {/* Botones */}
        <FormItem>
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={saveMutation.isPending}
              size="large"
            >
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button
                size="large"
                onClick={() => onNext(null)}
              >
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <Button
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              size="large"
              className="onboarding-draft-button"
            >
              {t('shared.buttons.saveDraft')}
            </Button>
            <Button
              onClick={handleResetForm}
              size="large"
            >
              {t('shared.buttons.cleanForm')}
            </Button>
            <ReportProblemButton stepLabel={t('shared.labels.personalData')} previewMode={previewMode} asButton />
          </Space>
        </FormItem>
      </Form>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={closePreview}
        document={previewDoc}
        title={previewTitle}
      />
    </div>
  );
}

export default IdentityStep;
